import React, { useEffect, useRef } from "react";
import { Box, Typography, Container, Grid } from "@mui/material";

import CodeIcon from "@mui/icons-material/Code";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import CampaignIcon from "@mui/icons-material/Campaign";
import AnalyticsIcon from "@mui/icons-material/Analytics";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// دیتای آپدیت‌شده با لحن کاملاً رسمی، B2B و توضیحات کامل
const businessServices = [
  {
    id: "web",
    title: "توسعه و مهندسی وب‌سایت",
    desc: "توسعه پلتفرم‌های سازمانی با معماری نرم‌افزاری مقیاس‌پذیر و عملکردی در تراز جهانی. ما با بهره‌گیری از فناوری‌های پیشرو و پیکربندی قدرتمند سمت سرور، وب‌سایت شما را نه صرفاً به عنوان یک ویترین، بلکه به عنوان هسته مرکزی و قدرتمند کسب‌وکارتان مهندسی می‌کنیم تا امنیت، سرعت و پایداری مطلق را برای سازمان شما در فضای دیجیتال تضمین کند.",
    icon: <CodeIcon sx={{ fontSize: 32 }} />,
    color: "#38BDF8", // آبی روشن - نماد تکنولوژی و اعتماد
  },
  {
    id: "seo",
    title: "بهینه‌سازی موتورهای جستجو (SEO)",
    desc: "تسلط بر نتایج جستجو و ارتقای جایگاه برند در فضای دیجیتال. با اجرای دقیق سئوی تکنیکال، استراتژی‌های داده‌محور برای کلمات کلیدی هدف و بهینه‌سازی ساختاری، ترافیک ارگانیک و باکیفیت را به صورت پایدار به سمت وب‌سایت شما هدایت می‌کنیم تا برند شما انتخاب بی‌چون‌وچرای مخاطبان در جستجوهای تخصصی باشد.",
    icon: <ManageSearchIcon sx={{ fontSize: 32 }} />,
    color: "#10B981", // سبز - نماد رشد و بازدهی
  },
  {
    id: "social",
    title: "معماری محتوا و راهبری شبکه‌های اجتماعی",
    desc: "جایگاه‌سازی و خلق اتوریته (اعتبار) برای برند در پلتفرم‌های دیجیتال. ما با عبور از روش‌های سنتی، به مهندسی پیام و خلق کمپین‌های محتوایی پربازده می‌پردازیم. استراتژی یکپارچه ما، رسانه‌های سازمان شما را به مراجع معتبری تبدیل می‌کند که ضمن ایجاد وفاداری عمیق، برند شما را به عنوان رهبر فکری (Thought Leader) صنعت خود معرفی می‌نماید.",
    icon: <CampaignIcon sx={{ fontSize: 32 }} />,
    color: "#8B5CF6", // بنفش - نماد خلاقیت و پرستیژ
  },
  {
    id: "analytics",
    title: "تحلیل داده‌های کلان و هوش تجاری",
    desc: "هدایت استراتژی‌های بازاریابی مبتنی بر داده‌های دقیق و قابل اتکا. با پایش مستمر رفتار کاربران، مانیتورینگ شبکه و تحلیل عمیق شاخص‌های کلیدی عملکرد (KPIs)، فرآیندهای بازاریابی به طور مداوم بهینه‌سازی می‌شوند. این رویکرد علمی منجر به حذف کامل هدررفتِ بودجه و تضمین بالاترین نرخ بازگشت سرمایه (ROI) خواهد شد.",
    icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
    color: "#F59E0B", // طلایی - نماد ارزش و هوش تجاری
  },
];

export default function ServicesList() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  const setCardRef = (el, index) => {
    cardsRef.current[index] = el;
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      cardsRef.current.forEach((card, index) => {
        gsap.fromTo(
          card,
          {
            opacity: 0,
            y: 50,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.15, // ایجاد افکت ظاهر شدن پشت سر هم
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%", // انیمیشن زمانی اجرا می‌شود که کارت به ۸۵ درصد از بالای صفحه برسد
            },
          }
        );
      });
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, []);

  const items = businessServices.map((service, index) => (
    <Grid size={{ xs: 12, lg: 6 }} key={service.id}>
      <Box
        ref={(el) => setCardRef(el, index)}
        sx={{
          position: "relative",
          bgcolor: "rgba(15, 23, 42, 0.4)",
          backdropFilter: "blur(12px)",
          p: { xs: 4, md: 5 },
          height: "100%",
          borderRadius: "24px",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          borderTop: `2px solid ${service.color}30`,
          transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
          overflow: "hidden",
          cursor: "pointer",
          "&:hover": {
            transform: "translateY(-8px)",
            bgcolor: "rgba(15, 23, 42, 0.7)",
            borderTop: `2px solid ${service.color}`,
            borderColor: "rgba(255, 255, 255, 0.1)",
            boxShadow: `0 30px 60px -15px ${service.color}15, 0 20px 40px -20px rgba(0,0,0,0.7)`,
            "& .service-title": {
              color: service.color,
            },
          },
        }}
      >
        {/* Decorative background glow inside the card */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "120px",
            height: "120px",
            background: `radial-gradient(circle, ${service.color}08 0%, transparent 70%)`,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {/* Icon Container */}
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "16px",
              bgcolor: `${service.color}10`,
              color: service.color,
              border: `1px solid ${service.color}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 4,
              boxShadow: `inset 0 0 12px ${service.color}05`,
            }}
          >
            {service.icon}
          </Box>

          {/* Service Title */}
          <Typography
            className="service-title"
            sx={{
              fontSize: "1.4rem",
              fontWeight: 700,
              mb: 2,
              color: "#F8FAFC",
              transition: "color 0.3s ease",
            }}
          >
            {service.title}
          </Typography>

          {/* Service Description */}
          <Typography
            sx={{
              fontSize: "1rem",
              color: "#94A3B8",
              lineHeight: 2.2, 
              // textAlign: "justify",
              letterSpacing: "-0.2px",
            }}
          >
            {service.desc}
          </Typography>
        </Box>
      </Box>
    </Grid>
  ));

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        overflow: "hidden",
        "& *": { direction: "rtl" }, // اطمینان از راست‌چین بودن کل المان‌های این بخش
      }}
    >
      <Container maxWidth="xl" sx={{ mb: 20 }}>
        <Grid container spacing={4}>
          {items}
        </Grid>
      </Container>
    </Box>
  );
}